import React, { useEffect, useState, useContext, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../Sidebar.css";
import { getHistory } from "../services/getHistory";
import { getInitials } from "../Helpers/stringHelpers";
import Tooltip from "./CustomTooltip";
import { AuthContext } from "../Hook/AuthProvider";
import Profilepopup from "./ProfilePopUp";
import { ROUTES } from "../constants/routes";
import { ChevronDown, ChevronUp, Shield } from "lucide-react";

const Sidebar = ({
  setShowPremiumButton,
  isStreamingDoneSidebar,
  sidebarclick,
  onSelectHistoryItem,
  isSidebarColl,
  setIsSidebarColl,
  refreshHistory,
  setRefreshHistory,
}) => {

  const [upgradeshow, setUpgradeshow] = useState(false);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [showStudyHubMenu, setShowStudyHubMenu] = useState(false);
  const studyHubMenuTimer = useRef(null);
  const { profileData, fetchProfile, isAuthenticated } = useContext(AuthContext);
  const profilePopupWrapperRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(false);
        getHistory()
          .then((response) => {
            setHistory(response);
          })
          .catch((error) => {
            console.error("Error:", error);
          });
      } catch (err) {
        setError("Error loading history");
      } finally {
        setLoading(false);
      }
    };
    if (history.length === 0) {
      loadHistory();
    }
  }, [history]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        setLoading(false);
        getHistory()
          .then((response) => {
            setHistory(response);
            setRefreshHistory();
          })
          .catch((error) => {
            console.error("Error:", error);
          });
      } catch (err) {
        setError("Error loading history");
      } finally {
        setLoading(false);
      }
    };
    if (refreshHistory) {
      loadHistory();
    }
  }, [refreshHistory]);

  useEffect(() => {
    if (isSidebarColl) {
      setIsHistoryOpen(false);
      closeStudyHubMenu();
    }
  }, [isSidebarColl]);

  useEffect(() => {
    return () => closeStudyHubMenu();
  }, []);

  const toggleSidebar = () => {
    setIsSidebarColl((prev) => !prev);
  };

  const toggleHistory = () => {
    setActiveMenu("history");
    if (isSidebarColl) {
      setIsSidebarColl(false);
      setIsHistoryOpen(true);
    } else {
      setIsHistoryOpen(!isHistoryOpen);
    }
  };

  const handleUserSectionClick = () => {
    setUpgradeshow(prev => !prev);
  };

  const [activeMenu, setActiveMenu] = useState("newChat");
  const [isStudyHubExpanded, setIsStudyHubExpanded] = useState(false);

  useEffect(() => {
    const path = location.pathname.toLowerCase();

    // Sync active menu with current route
    if (path.includes(ROUTES.DASHBOARD)) {
      setActiveMenu("dashboard");
      document.title = "Yooki";
    } else if (path.includes(ROUTES.CHAT)) {
      setActiveMenu("newChat");
      document.title = "Yooki";
    } else if (path.includes(ROUTES.NOTES)) {
      setActiveMenu("notes");
      setIsStudyHubExpanded(true);
    } else if (path.includes(ROUTES.CURRENT_AFFAIRS)) {
      setActiveMenu("currentAffairs");
      setIsStudyHubExpanded(true);
    } else if (path.includes(ROUTES.SYLLABUS)) {
      setActiveMenu("interactive");
      setIsStudyHubExpanded(true);
    } else if (path.includes(ROUTES.TESTS)) setActiveMenu("test");
    else if (path.includes(ROUTES.PLANNER)) {
      setActiveMenu("dailyplanner");
      setIsStudyHubExpanded(true);
    }else if (path.includes(ROUTES.HISTORY)) setActiveMenu("history");
    else if (path.includes(ROUTES.PROFILE)) setActiveMenu("profile");
    else if (path.includes("/admin")) setActiveMenu("admin");
    else setActiveMenu(""); // fallback
  }, [location.pathname]);

  // Retry profile fetch if authenticated but no profile data
  useEffect(() => {
    if (isAuthenticated && !profileData && !loading) {
      fetchProfile().catch(error => {
        console.error("Retry profile fetch failed:", error);
      });
    }
  }, [isAuthenticated, profileData, loading, fetchProfile]);

  const studyHubItems = [
    // { key: "missions", label: "My Missions", route: ROUTES.MISSIONS, icon: activeMenu === "missions" ? "/assets/images/menu_icon_mission_active.svg" : "/assets/images/menu_icon_mission.svg" },
    { key: "notes", label: "My Notes", route: ROUTES.NOTES, icon: activeMenu === "notes" ? "/assets/images/my_notes_new_sidebar_active.svg" : "/assets/images/my_notes_new_sidebar.svg" },
    { key: "currentAffairs", label: "Current Affairs", route: ROUTES.CURRENT_AFFAIRS, icon: activeMenu === "currentAffairs" ? "/assets/images/menu_icon_currentaffair_active.svg" : "/assets/images/menu_icon_currentaffair.svg" },
    { key: "interactive", label: "Interactive Syllabus", route: ROUTES.SYLLABUS, icon: activeMenu === "interactive" ? "/assets/images/interactive_new_sidebar_active.svg" : "/assets/images/interactive_new_sidebar.svg" },
    { key: "dailyplanner", label: "Study Planner", route: ROUTES.PLANNER, icon: activeMenu === "dailyplanner" ? "/assets/images/study_planner_new_sidebar_active.svg" : "/assets/images/study_planner_new_sidebar.svg" },
  ];

  const isStudyHubActive = ["notes", "currentAffairs", "interactive", "dailyplanner"].includes(activeMenu);

  const openStudyHubMenu = () => {
    if (studyHubMenuTimer.current) {
      clearTimeout(studyHubMenuTimer.current);
      studyHubMenuTimer.current = null;
    }
    setShowStudyHubMenu(true);
  };

  const scheduleStudyHubClose = () => {
    if (studyHubMenuTimer.current) {
      clearTimeout(studyHubMenuTimer.current);
    }
    studyHubMenuTimer.current = setTimeout(() => {
      setShowStudyHubMenu(false);
      studyHubMenuTimer.current = null;
    }, 250);
  };

  const closeStudyHubMenu = () => {
    if (studyHubMenuTimer.current) {
      clearTimeout(studyHubMenuTimer.current);
      studyHubMenuTimer.current = null;
    }
    setShowStudyHubMenu(false);
  };

  return (
    <div
      className="sidebar-wrapper"
      data-collapsed={isSidebarColl}
      ref={(el) => {
        sidebarclick.current = el;
        profilePopupWrapperRef.current = el;
      }}
    >
      <button
        className="sidebar-floating-toggle"
        onClick={() => setIsSidebarColl((prev) => !prev)}
        aria-label={isSidebarColl ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isSidebarColl ? "\u203A" : "\u2039"}
      </button>

      <div
        className={`sidebar sidebar-shell ${isSidebarColl ? "Iside isvisible" : "Notiside collapsed"}`}
        data-sidebar-state={isSidebarColl ? "collapsed" : "expanded"}
      >

        {/* 1. Dashboard */}
        <Tooltip text="Dashboard" position="right">
          <div
            className={`nav-link sidebar-menu-item ${activeMenu === "dashboard" ? "bg" : ""}`}
            onClick={() => navigate(ROUTES.DASHBOARD)}
          >
            <div className="sidebar-menu-content">
              <img
                src={
                  activeMenu === "dashboard"
                    ? "/assets/images/menu_icon_dashboard_active.svg"
                    : "/assets/images/menu_icon_dashboard.svg"
                }
                alt="Dashboard"
                className="sidebar-menu-icon"
              />
              <span className="open-sidebar-text">Dashboard</span>
            </div>
          </div>
        </Tooltip>

        {/* 2. New Chat */}
        <Tooltip text="New Chat" position="right">
          <div
            className={`nav-link sidebar-menu-item ${activeMenu === "newChat" ? "bg" : ""}`}
            onClick={() => {
              setShowPremiumButton(false);
              if (isStreamingDoneSidebar) {
                const flag = Math.floor(Math.random() * 1000000);
                navigate(ROUTES.CHAT, { state: { flag } });
                setActiveMenu("newChat");
              }
            }}
          >
            <div className="sidebar-menu-content">
              <img src="/assets/images/newchat2.svg" alt="New Chat" className="new-chat-icon-expanded sidebar-menu-icon" />
              <img src="/assets/images/newchat2.svg" alt="New Chat" className="new-chat-icon-collapsed sidebar-menu-icon" />
              <span className="open-sidebar-text">New Chat</span>
            </div>
          </div>
        </Tooltip>

        {/* 3. Study Hub (Collapsed Hover vs Expanded Toggle) */}
        {isSidebarColl ? (
          <div
            className="tooltip-container study-hub-collapsed-wrapper"
            onMouseEnter={openStudyHubMenu}
            onMouseLeave={scheduleStudyHubClose}
            style={{ position: "relative" }}
          >
            <div
              className={`nav-link sidebar-menu-item study-hub-collapsed-container ${isStudyHubActive ? "bg" : ""}`}
              style={{ position: "relative" }}
            >
              <div className="sidebar-menu-content">
                <img
                  src={
                    isStudyHubActive
                      ? "/assets/images/menu_icon_studyhub_active.svg"
                      : "/assets/images/menu_icon_studyhub.svg"
                  }
                  alt="Study Hub"
                  className="sidebar-menu-icon"
                />
                <span className="open-sidebar-text">Study Hub</span>
              </div>
            </div>

            {showStudyHubMenu && (
              <div
                className="study-hub-popup-menu"
                onMouseEnter={openStudyHubMenu}
                onMouseLeave={scheduleStudyHubClose}
              >
                {studyHubItems.map((item) => (
                  <div
                    key={item.key}
                    className={`study-hub-popup-item ${activeMenu === item.key ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(item.route);
                      closeStudyHubMenu();
                    }}
                  >
                    <img
                      src={item.icon}
                      alt={item.label}
                      className={`study-hub-subicon study-hub-${item.key}-icon`}
                    />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="study-hub-expanded-wrapper">
            <Tooltip text="Study Hub" position="right">
              <div
                className={`nav-link sidebar-menu-item ${isStudyHubActive ? "bg" : ""}`}
                onClick={(event) => {
                  event.stopPropagation();
                  setIsStudyHubExpanded((prev) => !prev);
                }}
                style={{ cursor: "pointer" }}
              >
                <div className="sidebar-menu-content study-hub-menu-content" style={{ display: "flex", alignItems: "center" }}>
                  <img
                    src={
                       isStudyHubActive
                         ? "/assets/images/menu_icon_studyhub_active.svg"
                         : "/assets/images/menu_icon_studyhub.svg"
                    }
                    alt="Study Hub"
                    className="sidebar-menu-icon study-hub-parent-icon"
                  />
                  <span className="open-sidebar-text">Study Hub</span>
                </div>
                <div
                  className="open-sidebar-text study-hub-arrow"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#64748b"
                  }}
                >
                  {isStudyHubExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </div>
              </div>
            </Tooltip>

            {isStudyHubExpanded && (
              <div className="study-hub-sub-items">
                {studyHubItems.map((item) => (
                  <Tooltip key={item.key} text={item.label} position="right">
                    <div
                      onClick={() => navigate(item.route)}
                      className={`nav-link sidebar-menu-item study-hub-sub-link ${activeMenu === item.key ? "bg" : ""}`}
                      style={{ paddingLeft: "28px" }}
                    >
                      <img
                        src={item.icon}
                        alt={item.label}
                        className={`sidebar-menu-icon study-hub-${item.key}-icon`}
                      />
                      <span className="open-sidebar-text">{item.label}</span>
                    </div>
                  </Tooltip>
                ))}
              </div>
            )}
          </div>
        )}

        {/* 4. My Tests */}
        <Tooltip text="My Tests" position="right">
          <div
            onClick={() => navigate(ROUTES.TESTS)}
            className={`nav-link sidebar-menu-item ${activeMenu === "test" ? "bg" : ""}`}
          >
            <div className="sidebar-menu-content">
              <img
                src={
                  activeMenu === "test"
                    ? "/assets/images/Prelim_test_new_sidebat _active.svg"
                    : "/assets/images/Prelim_test_new_sidebat.svg"
                }
                alt="My Tests"
                className="sidebar-menu-icon"
              />
              <span className="open-sidebar-text">My Tests</span>
            </div>
          </div>
        </Tooltip>

        {/* 5. My History */}
        <Tooltip text="My History" position="right">
          <div
            className={`nav-link sidebar-menu-item ${activeMenu === "history" ? "bg" : ""}`}
            onClick={() => navigate(ROUTES.HISTORY)}
            style={{ cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
          >
            <div
              className="sidebar-menu-content"
              style={{ flex: 1, display: "flex", alignItems: "center" }}
            >
              <img
                src={
                  activeMenu === "history"
                    ? "/assets/images/history_new_sidebar_icon_active.svg"
                    : "/assets/images/history_new_sidebar_icon.svg"
                }
                alt="History"
                className="sidebar-menu-icon"
              />
              <span className="open-sidebar-text">My History</span>
            </div>
            <div
              className="open-sidebar-text history-chevron-nav"
              onClick={(e) => {
                e.stopPropagation();
                toggleHistory();
              }}
              title="Expand history"
              style={{
                display: "flex",
                alignItems: "center",
                borderRadius: "4px",
                color: "#64748b",
                transition: "all 0.2s"
              }}
            >
              {isHistoryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>
          </div>
        </Tooltip>

        {/* History Section */}
        {isHistoryOpen && (
          <div className="history-section">
             {loading && (
               <div className="sidebar-history-skeleton" aria-label="Loading history" role="status">
                 {Array.from({ length: 4 }).map((_, index) => (
                   <span className="sidebar-history-skeleton-row" key={index} />
                 ))}
               </div>
             )}
            {error && <p>{error}</p>}
            <div className="history-list">
              {Object.entries(history).map(([key, items]) => (
                <div key={key}>
                  {items.length > 0 && (
                    <>
                      <div className="historyHeader">
                        {key.charAt(0).toUpperCase() + key.slice(1)}
                      </div>
                      <div className="header-bar"></div>
                    </>
                  )}
                  {items.map((item) => (
                    <div
                      key={item.conversation_id}
                      className="history-item"
                      onClick={() => {
                        onSelectHistoryItem(item.conversation_id);
                        toggleSidebar();
                         navigate(`${ROUTES.CHAT}/${item.conversation_id}`);
                      }}
                      dangerouslySetInnerHTML={{
                        __html:
                          item.message
                            .replace(/<[^>]+>/g, '') // Remove HTML tags
                            .split(' ')
                            .slice(0, 5)
                            .join(' ') +
                          (item.message.replace(/<[^>]+>/g, '').split(' ').length > 5 ? '...' : '')
                      }}
                    ></div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 6. Admin Panel (Placed under History icon) */}
        {/* 6. Admin Panel (Placed under History icon) */}
        {/* <Tooltip text="Admin Panel" position="right">
          <div
            className={`nav-link sidebar-menu-item ${activeMenu === "admin" ? "bg" : ""}`}
            onClick={() => navigate(ROUTES.ADMIN_QUESTIONS_BANK)}
            style={{ cursor: "pointer" }}
          >
            <div className="sidebar-menu-content">
              <div
                style={{
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: activeMenu === "admin" ? "#0284c7" : "#64748b",
                }}
              >
                <Shield size={19} />
              </div>
              <span className="open-sidebar-text">Admin Panel</span>
            </div>
          </div>
        </Tooltip> */}

        {/* User and Download Section */}
        {isAuthenticated && (
          <div style={{ marginTop: "auto" }} className="sidebar-footer">
            <span
              onClick={() => {
                window.open(
                  "https://yookistorage.blob.core.windows.net/assets/askyooki.apk",
                  "_blank"
                );
              }}
              className="download-btn-container"
            >
              <img
                src={isHistoryOpen ? "/assets/images/download-1.svg" : "/assets/images/downoladapp.svg"}
                alt="download"
                className="download-wide"
                style={{ width: "100%", display: "block", marginBottom: "10px", cursor: "pointer" }}
              />
              <img
                src={isHistoryOpen ? "/assets/images/download-1.svg" : "/assets/images/downoladapp.svg"}
                alt="download"
                className="download-narrow"
                style={{ width: "32px", display: "none", marginBottom: "10px", cursor: "pointer", marginLeft: "auto", marginRight: "auto" }}
              />
            </span>

            <div
              className="user-section"
              onClick={handleUserSectionClick}
              style={{ cursor: "pointer" }}
            >
              <div className="user-section-content" style={{ position: "relative" }}>
                <div className="user-info">
                  <div className="user-avatar sidebar-user-avatar">
                    {profileData?.user_type === "pro" ? (
                      <div
                        className={`menubar-wrapper profile-icon ${activeMenu === "profile" ? "boxy" : ""}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setUpgradeshow(prev => !prev);
                        }}
                      >
                        <div className="user-logo user-logo-profile mobile-profile custom-profile-logo">
                          <span>
                            {profileData?.first_name && profileData?.last_name
                              ? getInitials(`${profileData.first_name.trim()} ${profileData.last_name.trim()}`)
                              : ""}
                          </span>
                        </div>
                        <div className="proicon-open">
                          <img src="/assets/images/pro-button-gradient-1.svg" alt="" className="proicon-profileicon" />
                        </div>
                      </div>
                    ) : (
                      <div className="user-logo user-logo-profile custom-profile-logo">
                        <span>
                          {profileData?.first_name?.trim() && profileData?.last_name?.trim()
                            ? getInitials(`${profileData.first_name.trim()} ${profileData.last_name.trim()}`)
                            : "U"}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="user-details user-details-text">
                    <span className="user-name">{profileData?.first_name || "User"}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      {upgradeshow && <Profilepopup setUpgradeshow={setUpgradeshow} wrapperRef={profilePopupWrapperRef} />}
    </div>
  );
};

export default Sidebar;
